import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowRightLeft, GitMerge, MoreHorizontal, Trash2 } from 'lucide-react';
import { ApiError, api, type Me } from '../lib/api.js';
import { hasCapability } from '../lib/access.js';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Select,
  Textarea,
  useToast,
} from '../ui/index.js';

/**
 * The ticket's "More" menu: merge, transfer to another desk, delete. Each
 * shown only to someone who may do it on this desk (see Access → What each
 * role can do); nothing is offered that the API would refuse.
 */
export type ManageKind = 'merge' | 'transfer' | 'delete';
type Open = ManageKind | null;

/** Which of merge / transfer / delete this person may do on this desk. */
export function manageAbilities(me: Me | undefined, tenant: string): Record<ManageKind, boolean> {
  return {
    merge: hasCapability(me, tenant, 'ticket.merge'),
    transfer: hasCapability(me, tenant, 'ticket.transfer'),
    delete: hasCapability(me, tenant, 'ticket.delete'),
  };
}

/**
 * One of the three dialogs, for callers that open it from elsewhere — the
 * queue's right-click menu. `onDone` given: the caller stays where it is
 * (the queue refreshes) instead of being taken to the result.
 */
export function ManageDialog({
  kind,
  ...props
}: {
  kind: ManageKind;
  tenant: string;
  number: number;
  me: Me | undefined;
  onClose: () => void;
  onDone?: () => void;
  /** Merge only: the ticket to merge into, when the caller already knows it. */
  into?: number;
}) {
  if (kind === 'merge') return <MergeDialog {...props} />;
  if (kind === 'transfer') return <TransferDialog {...props} />;
  return <DeleteDialog {...props} />;
}

export function TicketManage({ tenant, number, me }: { tenant: string; number: number; me: Me | undefined }) {
  const [open, setOpen] = useState<Open>(null);
  const can = manageAbilities(me, tenant);
  if (!can.merge && !can.transfer && !can.delete) return null;

  return (
    <>
      <Menu>
        <MenuTrigger asChild>
          {/* Labelled, not a bare ⋯ — in the demo nobody found these. */}
          <Button variant="secondary" size="sm" leading={<MoreHorizontal />}>
            More
          </Button>
        </MenuTrigger>
        <MenuContent>
          {can.merge && (
            <MenuItem icon={<GitMerge />} onSelect={() => setOpen('merge')}>
              Merge into another ticket…
            </MenuItem>
          )}
          {can.transfer && (
            <MenuItem icon={<ArrowRightLeft />} onSelect={() => setOpen('transfer')}>
              Transfer to another desk…
            </MenuItem>
          )}
          {can.delete && (
            <MenuItem icon={<Trash2 />} danger onSelect={() => setOpen('delete')}>
              Delete ticket…
            </MenuItem>
          )}
        </MenuContent>
      </Menu>
      {open === 'merge' && <MergeDialog tenant={tenant} number={number} onClose={() => setOpen(null)} />}
      {open === 'transfer' && <TransferDialog tenant={tenant} number={number} me={me} onClose={() => setOpen(null)} />}
      {open === 'delete' && <DeleteDialog tenant={tenant} number={number} onClose={() => setOpen(null)} />}
    </>
  );
}

type DialogProps = { tenant: string; number: number; me?: Me | undefined; onClose: () => void; onDone?: () => void; into?: number };

function MergeDialog({ tenant, number, onClose, onDone, into: preset }: DialogProps) {
  const nav = useNavigate();
  const toast = useToast();
  const [into, setInto] = useState(preset ? String(preset) : '');
  const [reason, setReason] = useState('');
  const [tell, setTell] = useState(true);
  const go = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/tenants/${tenant}/tickets/${number}/merge`, {
        intoTicketNumber: Number(into),
        reason,
        tellRequester: tell,
      }),
    onSuccess: () => {
      toast.success(
        `Merged into #${into}`,
        `#${number} is cancelled and linked to it.${tell ? ' Its requester has been told.' : ''}`,
      );
      onClose();
      if (onDone) onDone();
      else nav(`/t/${tenant}/${into}`);
    },
    onError: (e) => toast.error('Not merged', e instanceof ApiError ? e.message : 'the merge failed'),
  });
  const ready = /^\d+$/.test(into.trim()) && Number(into) !== number && reason.trim() !== '';
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Merge #${number} into another ticket`}
        description="Use when the same request arrived twice. This one is cancelled and linked to the other, where the work carries on; a note there says so."
      >
        <div className="space-y-3">
          <Field label="Into ticket number" required>
            <Input value={into} onChange={(e) => setInto(e.target.value.replace(/[^\d]/g, ''))} placeholder="120221" inputMode="numeric" />
          </Field>
          <Field label="Why" required>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Same request, sent twice" />
          </Field>
          <Checkbox
            checked={tell}
            onCheckedChange={(v) => setTell(v === true)}
            label={`Email the requester of #${number} that it carries on as #${into || '…'}`}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => go.mutate()} loading={go.isPending} disabled={!ready}>
            Merge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ tenant, number, me, onClose, onDone }: DialogProps) {
  const nav = useNavigate();
  const toast = useToast();
  const desks = (me?.tenants ?? []).filter((t) => t.id !== tenant);
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  // The receiving desk's required fields, when it has any the ticket lacks.
  const [needs, setNeeds] = useState<{ key: string; message: string }[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const go = useMutation({
    mutationFn: () =>
      api.post<{ newNumber: number }>(`/api/v1/tenants/${tenant}/tickets/${number}/transfer`, {
        toTenant: to,
        reason,
        custom: Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim() !== '')),
      }),
    onSuccess: (r) => {
      const name = desks.find((d) => d.id === to)?.name ?? to;
      toast.success(`Transferred to ${name}`, `It is #${r.newNumber} there.`);
      onClose();
      if (onDone) onDone();
      else nav(`/t/${to}/${r.newNumber}`);
    },
    onError: (e) => {
      const fields = e instanceof ApiError ? e.fields : undefined;
      if (fields && fields.length) {
        setNeeds(fields.map((f) => ({ key: f.key, message: f.message })));
        return;
      }
      toast.error('Not transferred', e instanceof ApiError ? e.message : 'the transfer failed');
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Transfer #${number} to another desk`}
        description="For a request that belongs somewhere else. It becomes a new ticket on that desk, with the conversation, and this one is linked to it."
      >
        <div className="space-y-3">
          <Field label="To" required>
            <Select
              value={to}
              onValueChange={(v) => {
                setTo(v);
                setNeeds([]);
                setValues({});
              }}
              options={desks.map((d) => ({ value: d.id, label: d.name }))}
              placeholder="Choose a desk"
              aria-label="Transfer to"
            />
          </Field>
          <Field label="Why" required>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="This is a payroll question, not IT" />
          </Field>
          {needs.length > 0 && (
            <div className="space-y-2 rounded-md border border-line p-3">
              <p className="text-2xs text-ink-secondary">That desk needs these before it will take the ticket:</p>
              {needs.map((n) => (
                <Field key={n.key} label={n.message.replace(/ is required$/, '')}>
                  <Input value={values[n.key] ?? ''} onChange={(e) => setValues({ ...values, [n.key]: e.target.value })} />
                </Field>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => go.mutate()} loading={go.isPending} disabled={!to || reason.trim() === ''}>
            Transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ tenant, number, onClose, onDone }: DialogProps) {
  const nav = useNavigate();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const go = useMutation({
    mutationFn: () => api.del(`/api/v1/tenants/${tenant}/tickets/${number}`, { reason }),
    onSuccess: () => {
      toast.success(`#${number} deleted`, 'It can be restored from Deleted tickets.');
      onClose();
      if (onDone) onDone();
      else nav('/queue');
    },
    onError: (e) => toast.error('Not deleted', e instanceof ApiError ? e.message : 'the delete failed'),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Delete #${number}`}
        description="It disappears from the queue, search and reports. Nothing is thrown away: it can be restored, exactly as it was, from Deleted tickets."
      >
        <Field label="Why" required hint="The only record of why it went.">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Vendor spam" />
        </Field>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => go.mutate()} loading={go.isPending} disabled={reason.trim() === ''}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
